import React from "react";
import { injectIntl } from "react-intl";
import connect from "react-redux/es/connect/connect";
import { Button, message } from "antd";
import { assignToMe, markConversationRead, setAssigned, setChatId, setTabId } from "../../../../appRedux/actions";
import { sortMessagesMyQueuedTeam } from "../utils";
import { formatWait, oldestWaitingFirst, useNow, waitingMs } from "./inbox";
import { takeConversation } from "./actions";

// The thread pane while no conversation is open. In the queue it says how many customers wait and how long
// the oldest has waited, with "Take next"; elsewhere it is a quiet prompt to pick a conversation.
const InboxEmptyPane = ({intl, classList, global, messages, assignToMe, setAssigned, setChatId, setTabId, markConversationRead}) => {
    const t = (id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);
    const now = useNow(30000);
    const sorted = sortMessagesMyQueuedTeam(messages || []);
    const queuedTab = String(global.tabId || '2') === '2';
    const queue = oldestWaitingFirst(sorted.queued, now);
    const waiting = queue.filter(conversation => waitingMs(conversation, now) !== null);
    const oldest = waiting[0] || null;
    const mineUnread = sorted.my.filter(conversation => !conversation.isRead).length;

    const takeNext = () => {
        const next = oldest || queue[0];
        if (!next) return;
        takeConversation(next, {assignToMe, setAssigned, setChatId, setTabId});
        if (!next.isRead && next.conversationId) markConversationRead(next.conversationId);
        message.success(t('chats.conversationAssigned', 'Conversation assigned'));
    };

    const keys = (
        <p className="inbox-empty__keys">
            <kbd>J</kbd><kbd>K</kbd> {t('chats.keys.move', 'move')} · <kbd>/</kbd> {t('chats.keys.search', 'search')}
        </p>
    );

    let content;
    if (queuedTab && queue.length) {
        content = (
            <>
                <p className="inbox-empty__kicker">{t('chat.queued', 'Queued')}</p>
                <h2 className="inbox-empty__title">
                    {t('chats.empty.waitingTitle', '{n, plural, one {# person is waiting} other {# people are waiting}}', {n: waiting.length || queue.length})}
                </h2>
                <p className="inbox-empty__text">
                    {oldest
                        ? t('chats.empty.oldest', 'The oldest has waited {time}. Take the next one or pick from the list.', {time: formatWait(intl, waitingMs(oldest, now))})
                        : t('chats.empty.queuedText', 'Take the next one or pick from the list.')}
                </p>
                <Button type="primary" size="large" className="inbox-empty__cta" onClick={takeNext}>
                    {t('chats.empty.takeNext', 'Take next')}
                </Button>
                <div className="inbox-empty__stats">
                    <div><span>{t('chat.queued', 'Queued')}</span><b className="is-accent">{queue.length}</b></div>
                    <div><span>{t('chats.empty.mineUnread', 'Mine, unread')}</span><b>{mineUnread}</b></div>
                </div>
                {keys}
            </>
        );
    } else if (queuedTab) {
        content = (
            <>
                <p className="inbox-empty__kicker">{t('chat.queued', 'Queued')}</p>
                <h2 className="inbox-empty__title">{t('chats.empty.queueEmptyTitle', 'Nobody is waiting')}</h2>
                <p className="inbox-empty__text">{t('chats.empty.queueEmptyText', 'New chats from customers appear here until someone takes them.')}</p>
                {keys}
            </>
        );
    } else {
        content = (
            <>
                <h2 className="inbox-empty__title">{t('chats.empty.selectTitle', 'Select a conversation')}</h2>
                <p className="inbox-empty__text">{t('chats.empty.selectText', 'Pick a conversation from the list to read and reply.')}</p>
                {keys}
            </>
        );
    }

    return (
        <div className={`chat-container inbox-empty ${classList || ''}`}>
            <div className="inbox-empty__inner">{content}</div>
        </div>
    );
};

const mapStateToProps = ({global, messages}) => ({global, messages});

export default injectIntl(connect(mapStateToProps, {assignToMe, setAssigned, setChatId, setTabId, markConversationRead})(InboxEmptyPane));
